import { SearchFilterCondition } from 'lib-search-types';
import { useMemo } from 'react';

import { searchApi } from 'apis/search';

import type {
  DispatchProps,
  StateProps,
} from '../containers/transferToAgentModal';
import { rankSearchMember } from '../utils/rankSearchMember';

import TransferToAgentModal from './transferToAgentModal';

type TransferToAgentModalWrapperProps = StateProps & DispatchProps;

export function TransferToAgentModalWrapper(
  props: TransferToAgentModalWrapperProps,
): JSX.Element {
  const { queueId, profile, ...restProps } = props;

  const filters: SearchFilterCondition<'users'>[] = useMemo(() => {
    const base: SearchFilterCondition<'users'>[] = [
      { field: 'user_type', operator: 'is_one', values: [{ value: 'member' }] },
      { field: 'is_online', operator: 'equals', values: [{ value: 'true' }] },
    ];
    return queueId
      ? [
          ...base,
          {
            field: 'queues',
            operator: 'includes_one',
            values: [{ value: queueId }],
          },
        ]
      : base;
  }, [queueId]);

  const { onlineMembers, isLoading } =
    searchApi.useReadSearchUsersEnrichedQuery(
      {
        scope: 'users',
        filters: { strategy: 'all', filters },
        sortBy: { field: 'display_name', order: 'asc' },
        pageSize: 1000,
        enrichmentSpec: ['workload_config'],
      },
      {
        selectFromResult: ({ data, isLoading, isError }) => ({
          onlineMembers: data?.data.results.map(({ result }) => result) ?? [],
          isLoading,
          isError,
        }),
      },
    );

  const sortedMembers = useMemo(
    () =>
      [...onlineMembers].sort((a, b) => {
        const rankDiff = rankSearchMember(a) - rankSearchMember(b);
        if (rankDiff !== 0) return rankDiff;
        return (a.name ?? '')
          .toLowerCase()
          .localeCompare((b.name ?? '').toLowerCase());
      }),
    [onlineMembers],
  );

  return (
    <TransferToAgentModal
      {...restProps}
      profile={profile}
      queueId={queueId}
      onlineMembers={sortedMembers}
      isLoadingMembers={isLoading}
    />
  );
}
